import {Dialog, DialogBackdrop, DialogPanel, DialogTitle, Description} from "@headlessui/react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTimes} from "@fortawesome/free-solid-svg-icons";
import {useBoundStore} from "../../../Store/store";
import * as React from "react";
import {useForm} from "react-hook-form";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup";
import {PrimaryButton} from "../../Buttons/PrimaryButton";
import {SelectInput} from "../../Form/SelectInput";
import {DateTimePickerInput} from "../../Form/DateTimePickerInput";
import {useEffect, useState} from "react";
import {SecondaryButton} from "../../Buttons/SecondaryButton";
import {useTranslation} from "react-i18next";

export const GeneratePerformanceStatementDialog = (props) => {
    const {
        isOpen,
        onClose,
        freelancerId,
        onSuccess
    } = props;

    const {t} = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [branches, setBranches] = useState([]);

    const generatePerformanceStatement = useBoundStore((state) => state.generatePerformanceStatement);
    const getMyBranchesNotPaginated = useBoundStore((state) => state.getMyBranchesNotPaginated);
    const branchesNotPaginated = useBoundStore((state) => state.branchesNotPaginated);

    useEffect(() => {
        if (isOpen) {
            getMyBranchesNotPaginated();
        }
    }, [isOpen, getMyBranchesNotPaginated]);

    useEffect(() => {
        if (branchesNotPaginated && branchesNotPaginated.length > 0) {
            const branchOptions = branchesNotPaginated.map(branch => ({
                value: branch.id,
                label: branch.company_name,
                id: branch.id
            }));
            setBranches(branchOptions);
        }
    }, [branchesNotPaginated]);

    const schema = yup.object({
        branch_id: yup.number().required(t('performance_reports.select_branch')),
        date: yup.date().required(t('performance_reports.select_date')),
    }).required();

    const {
        register,
        handleSubmit,
        formState: {errors},
        setValue,
        control,
        reset
    } = useForm({
        resolver: yupResolver(schema),
        reValidateMode: 'onBlur',
        mode: 'onChange',
        defaultValues: {
            branch_id: null,
            date: null,
        }
    });

    const onSubmit = async (data) => {
        setIsLoading(true);
        const d = data.date instanceof Date ? data.date : new Date(data.date);
        const payload = {
            branch_id: data.branch_id,
            date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`,
        };
        const result = await generatePerformanceStatement(freelancerId, payload);
        setIsLoading(false);

        if (result.success) {
            reset();
            onClose();
            if (onSuccess) {
                onSuccess();
            }
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity duration-300 ease-in-out data-[closed]:opacity-0"
            />
            <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
                <DialogPanel className="w-full md:w-1/3 space-y-4 border bg-white p-12 rounded-lg">
                    <div className="flex items-start justify-between flex-wrap">
                        <DialogTitle className="font-bold text-center text-xl">
                            {t('performance_reports.generate')}
                        </DialogTitle>
                        <div className="ml-3 flex h-7 items-center">
                            <button
                                type="button"
                                className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                                onClick={handleClose}
                                disabled={isLoading}
                            >
                                <span className="absolute -inset-2.5"/>
                                <span className="sr-only">Close panel</span>
                                <FontAwesomeIcon icon={faTimes} className="h-6 w-6" aria-hidden="true"/>
                            </button>
                        </div>
                    </div>
                    <Description className="text-sm text-gray-600">
                        {t('dialogs.generate_performance.description')}
                    </Description>

                    <form className={'grid grid-cols-1'} onSubmit={handleSubmit(onSubmit)}>
                        <div className="mb-2">
                            <SelectInput
                                control={control}
                                name={'branch_id'}
                                translationIndex={'general.fields.branch'}
                                options={branches}
                                errors={errors}
                                setValue={setValue}
                                clearable={false}
                            />
                        </div>

                        <div className="mb-2">
                            <DateTimePickerInput
                                control={control}
                                name={'date'}
                                translationIndex={'general.fields.month'}
                                errors={errors}
                                setValue={setValue}
                                dateFormat="MM/yyyy"
                                showTimeInput={false}
                                isClearable={false}
                                showMonthYearPicker={true}
                            />
                        </div>

                        <div className="flex flex-row space-x-4 mt-4">
                            <SecondaryButton onClick={handleClose} outlined={true} disabled={isLoading}>
                                {t('general.actions.cancel')}
                            </SecondaryButton>
                            <PrimaryButton onClick={handleSubmit(onSubmit)} disabled={isLoading}>
                                {isLoading ? (
                                    <div className="flex items-center gap-2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        {t('performance_reports.generating')}
                                    </div>
                                ) : (
                                    t('general.actions.generate')
                                )}
                            </PrimaryButton>
                        </div>
                    </form>
                </DialogPanel>
            </div>
        </Dialog>
    );
};

